/**
 * Черновик текстового фильтра: поле ввода отзывается на каждую букву,
 * «настоящее» значение догоняет с паузой.
 *
 * Появился на общем экране полей (AK-750), вынесен в общий модуль под
 * карточку хозяйства (AK-746) — там та же беда: без паузы каждая буква в
 * поиске отправляла запрос на список и попутно сбрасывала отметки чекбоксов,
 * то есть человек терял выбор, пока дописывал слово.
 *
 * Двусторонняя синхронизация — место, где легко потерять ввод: если слепо
 * тянуть черновик за внешним значением, то собственный отложенный push
 * («Топ») откатит уже набранное («Тополёк»). Поэтому помним, ЧТО мы сами
 * отправили: значение, совпадающее с нашей последней отправкой, черновик не
 * трогает; любое другое изменение снаружи (назад/вперёд, «сбросить фильтры»,
 * открытая ссылка) — трогает.
 *
 * `push` не обязан вести в адрес: на общем экране полей он пишет в
 * search-параметры, в карточке хозяйства — в состояние запроса. Хук про
 * паузу и сохранность ввода, а не про адресную строку.
 */
import { useEffect, useRef, useState } from 'react';

/**
 * Пауза перед тем, как набранное уедет дальше. Раньше запрос уходил на КАЖДУЮ
 * букву; там, где значение попадает ещё и в историю браузера, пауза тем более
 * обязательна.
 */
export const TEXT_FILTER_DEBOUNCE_MS = 300;

export function useTextFilterDraft(
  externalValue: string,
  push: (value: string) => void,
): [string, (value: string) => void] {
  const [draft, setDraft] = useState(externalValue);
  const pushedRef = useRef(externalValue);

  useEffect(() => {
    if (externalValue === pushedRef.current) return;
    pushedRef.current = externalValue;
    setDraft(externalValue);
  }, [externalValue]);

  useEffect(() => {
    if (draft === pushedRef.current) return;
    const timer = setTimeout(() => {
      pushedRef.current = draft;
      push(draft);
    }, TEXT_FILTER_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [draft, push]);

  return [draft, setDraft];
}
